import React, { FunctionComponent, useEffect, useState } from "react";
import { HubConnection } from "@microsoft/signalr";
import {
	Box,
	Button,
	Divider,
	Stack,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
} from "@mui/material";
import {
	HubMessageNotification,
	HubMessageNotificationType,
} from "Infrastructure/Models/Messages/MessageNotification";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CircularProgress from "@mui/material/CircularProgress";
import { LogEntry } from "Shared/LogWindow/LogController";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface Props {
	actionName: string;
	requestId?: string | null;
	deviceNames: string[] | null;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

export interface CompletedDevice {
	deviceName: string;
	responseMessage: string | null;
	createdTime: string;
	resultType: HubMessageNotificationType;
}

const InstanceResponseContent: FunctionComponent<Props> = ({
	actionName,
	requestId,
	deviceNames,
	hubConnection,
	closeDialog,
}) => {
	const [completedDevices, setCompletedDevices] = useState<
		Map<string, CompletedDevice>
	>(new Map());
	const [timedOutDevices, setTimedOutDevices] = useState<Set<string>>(
		new Set()
	);

	if (!requestId || !deviceNames || deviceNames.length === 0) {
		closeDialog();
		return null;
	}

	const handleCopyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			console.error("Failed to copy to clipboard:", err);
		}
	};

	useEffect(() => {
		// Get existing logs for this requestId
		const storedLogs: LogEntry[] = JSON.parse(
			localStorage.getItem("serverLogs") || "[]"
		);
		const relevantLogs = storedLogs.filter(
			(log) =>
				log.notification?.requestId === requestId &&
				log.notification.isReceivedMessage &&
				log.notification.deviceNames?.length === 1
		);

		// Process existing logs
		const newCompletedDevices = new Map<string, CompletedDevice>();
		for (const log of relevantLogs) {
			if (log.notification?.deviceNames?.[0]) {
				const deviceName = log.notification.deviceNames[0];
				newCompletedDevices.set(deviceName, {
					deviceName,
					responseMessage: log.notification.message,
					createdTime: log.timestamp,
					resultType: log.notification.type,
				});
			}
		}
		setCompletedDevices(newCompletedDevices);

		// Listen for new logs
		const handleNewLog = (event: Event) => {
			const customEvent = event as CustomEvent<{
				message: string;
				notification?: HubMessageNotification | null;
			}>;

			const notification = customEvent.detail.notification;
			if (
				notification?.requestId === requestId &&
				notification.isReceivedMessage &&
				notification.deviceNames?.length === 1
			) {
				const deviceName = notification.deviceNames[0];
				setCompletedDevices((prev) =>
					new Map(prev).set(deviceName, {
						deviceName,
						responseMessage: notification.message,
						createdTime: new Date().toLocaleTimeString([], {
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
							hour12: false,
						}),
						resultType: notification.type,
					})
				);
			}
		};

		document.addEventListener("addLog", handleNewLog as EventListener);

		// Set up timeouts only for devices that haven't completed yet
		const timeouts = new Map<string, NodeJS.Timeout>();
		for (const deviceName of deviceNames) {
			// Check if device is already completed using the processed logs
			const isCompleted = newCompletedDevices.has(deviceName);

			if (!isCompleted) {
				timeouts.set(
					deviceName,
					setTimeout(() => {
						setTimedOutDevices((prev) => {
							// Check if device is still not completed before adding to timed out
							setCompletedDevices((currentCompleted) => {
								if (!currentCompleted.has(deviceName)) {
									return currentCompleted;
								}
								return currentCompleted;
							});

							// Add to timed out devices if not completed
							return new Set(prev).add(deviceName);
						});
					}, 300000)
				); // 5 minutes
			}
		}

		return () => {
			document.removeEventListener("addLog", handleNewLog as EventListener);
			for (const timeout of timeouts.values()) {
				clearTimeout(timeout);
			}
		};
	}, [requestId, deviceNames]);

	return (
		<Stack>
			<Box mb={1.5}>
				<Divider />
			</Box>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Box ml={1.5}>
					<Typography variant="h6" component="div">
						{actionName}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
						<Chip
							label={
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<span>{requestId}</span>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleCopyToClipboard(requestId);
										}}
										sx={{
											padding: 0,
											"& .MuiSvgIcon-root": {
												fontSize: "0.9rem",
											},
										}}
									>
										<ContentCopyIcon />
									</IconButton>
								</Box>
							}
							size="medium"
							variant="outlined"
							sx={{ fontSize: "0.9rem", height: 28 }}
						/>
					</Box>
				</Box>
			</Stack>
			<TableContainer component={Paper} sx={{ width: "100%", mt: 2 }}>
				<Table
					size="small"
					sx={{
						width: "100%",
						"& .MuiTableCell-root": { padding: "6px 10px" },
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: "18px" }} />
							<TableCell sx={{ width: "164px" }}>Instance</TableCell>
							<TableCell sx={{ flex: 1 }}>Response – Message</TableCell>
							<TableCell sx={{ width: "100px" }}>Čas</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{deviceNames.map((deviceName) => {
							const completedDevice = completedDevices.get(deviceName);
							const isTimedOut = timedOutDevices.has(deviceName);
							return (
								<TableRow key={deviceName}>
									<TableCell sx={{ width: "18px" }}>
										<Stack direction="row" alignItems="center" justifyContent="center">
											{isTimedOut ? (
												<ErrorIcon
													color="warning"
													sx={{ fontSize: "1.1rem" }}
												/>
											) : completedDevice ? (
												completedDevice.resultType !==
												HubMessageNotificationType.Error ? (
													<CheckCircleIcon
														color="success"
														sx={{ fontSize: "1.1rem" }}
													/>
												) : (
													<ErrorIcon
														color="error"
														sx={{ fontSize: "1.1rem" }}
													/>
												)
											) : (
												<CircularProgress color="inherit" size="0.9rem" />
											)}
										</Stack>
									</TableCell>
									<TableCell sx={{ width: "164px" }}>
										<Chip
											label={deviceName}
											size="small"
											variant="outlined"
											sx={{ fontSize: "0.8rem", height: 20 }}
										/>
									</TableCell>

									<TableCell sx={{ flex: 1 }}>
										<Typography
											variant="caption"
											sx={{ color: "text.secondary" }}
										>
											{isTimedOut
												? "Vypršel časový limit ke zpracování požadavku. Zkontrolujte data instance."
												: completedDevice && !!completedDevice.responseMessage
												? completedDevice.responseMessage
												: "–"}
										</Typography>
									</TableCell>
									<TableCell sx={{ width: "100px" }}>
										<Typography
											variant="caption"
											sx={{ color: "text.secondary" }}
										>
											{isTimedOut
												? "–"
												: completedDevice
												? completedDevice.createdTime
												: "–"}
										</Typography>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
};

export default InstanceResponseContent;
